import { useEffect, useState } from "react";

/**
 * Cycle a fixed-size window over a list on a timer. Every `intervalMs` the
 * window advances by its own size and wraps around, so over time a viewer sees
 * the whole list a batch at a time. Used on the home page to rotate through all
 * photos / posts / videos with a relaxed delay.
 *
 * Returns a slice of `windowSize` items starting at the current offset (wrapping
 * past the end). If there's nothing to rotate (list shorter than the window),
 * it just returns the list and never starts a timer.
 */
export function useRotatingWindow<T>(
  items: T[],
  windowSize: number,
  intervalMs: number,
): T[] {
  const [offset, setOffset] = useState(0);

  useEffect(() => {
    if (items.length <= windowSize) {
      setOffset(0);
      return;
    }
    const id = setInterval(() => {
      setOffset((o) => (o + windowSize) % items.length);
    }, intervalMs);
    return () => clearInterval(id);
  }, [items.length, windowSize, intervalMs]);

  if (items.length === 0) return [];
  const count = Math.min(windowSize, items.length);
  const out: T[] = [];
  for (let i = 0; i < count; i++) {
    out.push(items[(offset + i) % items.length]);
  }
  return out;
}
